import { VscodeCollapsible } from "@vscode-elements/react-elements";
import clsx from "clsx";
import { FC, ReactNode, useCallback } from "react";

import { MarkdownReference } from "@tsmono/react/components";
import { useCollapsibleIds } from "@tsmono/react/hooks";
import { isRecord } from "@tsmono/util";

import {
  kDefaultResolvedView,
  ResolvedField,
  ResolvedScannerResultView,
} from "../sentinels/viewerConfig";

import { Explanation } from "./Explanation";
import { Metadata, MetadataValue } from "./Metadata";
import styles from "./ScannerResultDetailView.module.css";
import { ScanResultInput } from "./types";
import { ValidationResult } from "./ValidationResult";
import { Value } from "./Value";

interface ScannerResultDetailViewProps {
  data: ScanResultInput;
  references?: MarkdownReference[];
  header?: ReactNode;
  interactive?: boolean;
  options?: {
    previewRefsOnHover?: boolean;
  };
  /**
   * Order, labels, promoted-metadata entries, and metadata-key exclusions to
   * apply when rendering. Omit to use `kDefaultResolvedView` (built-in order,
   * no promotions, no exclusions).
   */
  config?: ResolvedScannerResultView;
}

const kCollapseScope = "scanner-result-sections";

export const ScannerResultDetailView: FC<ScannerResultDetailViewProps> = ({
  data,
  references,
  header,
  interactive = false,
  options,
  config = kDefaultResolvedView,
}) => {
  const [collapsedIds, setCollapsed] = useCollapsibleIds(kCollapseScope);
  const onToggle = useCallback(
    (id: string, open: boolean) => setCollapsed(id, !open),
    [setCollapsed]
  );

  return (
    <div className={clsx(styles.container, "text-size-small")}>
      {header ? <div className={styles.header}>{header}</div> : null}
      {config.fields.map((field) => {
        const rendered = renderField(
          field,
          data,
          references,
          options,
          interactive,
          config,
          collapsedIds,
          onToggle
        );
        if (!rendered) return null;
        return <FieldFragment key={fieldId(field)}>{rendered}</FieldFragment>;
      })}
    </div>
  );
};

const FieldFragment: FC<{ children: ReactNode }> = ({ children }) => (
  <>{children}</>
);

// ---------------------------------------------------------------------------
// Section-level rendering
// ---------------------------------------------------------------------------

interface SectionProps {
  id: string;
  heading: string;
  open: boolean;
  onToggle: (id: string, open: boolean) => void;
  children: ReactNode;
}

const Section: FC<SectionProps> = ({
  id,
  heading,
  open,
  onToggle,
  children,
}) => {
  const attachToggleListener = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      const handler = (e: Event) => {
        // The web component's toggle event; anything else on this name is not
        // ours to act on.
        if (!(e instanceof CustomEvent)) return;
        const detail: unknown = e.detail;
        if (!isRecord(detail) || typeof detail["open"] !== "boolean") return;
        onToggle(id, detail["open"]);
      };
      el.addEventListener("vsc-collapsible-toggle", handler);
      return () => el.removeEventListener("vsc-collapsible-toggle", handler);
    },
    [id, onToggle]
  );

  return (
    <VscodeCollapsible
      ref={attachToggleListener}
      heading={heading}
      open={open}
      className={styles.section}
    >
      {children}
    </VscodeCollapsible>
  );
};

// ---------------------------------------------------------------------------
// Field dispatch
// ---------------------------------------------------------------------------

function fieldId(field: ResolvedField): string {
  return field.kind === "builtin"
    ? `builtin:${field.name}`
    : `metadata:${field.key}`;
}

function sectionOpen(
  field: ResolvedField,
  collapsedIds: Record<string, boolean> | undefined
): boolean {
  // Persisted user toggle wins; otherwise respect the descriptor's `collapsed`
  // default, which defaults to false ("open").
  const toggled = collapsedIds?.[fieldId(field)];
  if (toggled === undefined) return !field.collapsed;
  return !toggled;
}

function renderField(
  field: ResolvedField,
  data: ScanResultInput,
  references: MarkdownReference[] | undefined,
  options: { previewRefsOnHover?: boolean } | undefined,
  interactive: boolean,
  config: ResolvedScannerResultView,
  collapsedIds: Record<string, boolean> | undefined,
  onToggle: (id: string, open: boolean) => void
): ReactNode {
  const id = fieldId(field);
  const open = sectionOpen(field, collapsedIds);

  if (field.kind === "metadata") {
    const value = data.metadata?.[field.key];
    if (value === undefined) return null;
    return (
      <Section
        id={id}
        heading={field.label ?? field.key}
        open={open}
        onToggle={onToggle}
      >
        <MetadataValue
          id={field.key}
          value={value}
          references={references}
          options={options}
        />
      </Section>
    );
  }

  switch (field.name) {
    case "label":
      if (!data.label) return null;
      return (
        <Section
          id={id}
          heading={field.label ?? "Label"}
          open={open}
          onToggle={onToggle}
        >
          <div>{data.label}</div>
        </Section>
      );

    case "value":
      return (
        <Section
          id={id}
          heading={field.label ?? "Value"}
          open={open}
          onToggle={onToggle}
        >
          <Value
            value={data.value}
            valueType={data.valueType}
            identifier={data.identifier}
            style="block"
            maxTableSize={1000}
            interactive={interactive}
            references={references}
            options={options}
          />
        </Section>
      );

    case "validation":
      if (
        data.validationResult === undefined ||
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        data.validationResult === null
      ) {
        return null;
      }
      return (
        <Section
          id={id}
          heading={field.label ?? "Validation"}
          open={open}
          onToggle={onToggle}
        >
          <ValidationResult
            result={data.validationResult}
            target={data.validationTarget}
            label={data.label}
          />
        </Section>
      );

    case "answer":
      if (!data.answer) return null;
      return (
        <Section
          id={id}
          heading={field.label ?? "Answer"}
          open={open}
          onToggle={onToggle}
        >
          <div>{data.answer}</div>
        </Section>
      );

    case "explanation":
      if (!data.explanation) return null;
      return (
        <Section
          id={id}
          heading={field.label ?? "Explanation"}
          open={open}
          onToggle={onToggle}
        >
          <Explanation
            explanation={data.explanation}
            references={references}
            options={options}
          />
        </Section>
      );

    case "metadata": {
      if (data.metadata === undefined) return null;
      const excluded = new Set(config.excludedMetadataKeys);
      const remaining = Object.keys(data.metadata).filter(
        (k) => !excluded.has(k)
      );
      if (remaining.length === 0) return null;
      return (
        <Section
          id={id}
          heading={field.label ?? "Metadata"}
          open={open}
          onToggle={onToggle}
        >
          <Metadata
            metadata={data.metadata}
            references={references}
            options={options}
            excludeKeys={config.excludedMetadataKeys}
          />
        </Section>
      );
    }
  }
}
